import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

function parseDateString(dateStr) {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getMonday(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay();
    // Sunday is 0, Monday is 1, ..., Saturday is 6
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d;
}

function getWeekDaysForMonday(monday) {
    const days = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
        days.push(d);
    }
    return days;
}

export default function FriendlyDateSelector({
    value,
    onChange,
    label,
    maxDate = formatDateString(new Date()),
    className = "",
}) {
    const { i18n } = useTranslation();
    const lang = i18n.language || "es";

    const weekScrollRef = useRef(null);
    const isProgrammaticScroll = useRef(false);

    const todayStr = useMemo(() => formatDateString(new Date()), []);

    // Default to today if no date is saved yet
    useEffect(() => {
        if (!value && onChange) {
            onChange(todayStr);
        }
    }, [value, todayStr, onChange]);

    // Generate week pages (carousel of 7-day weeks)
    const weekPages = useMemo(() => {
        const today = new Date();
        const currentMonday = getMonday(today);

        let weeksBack = 12; // 3 months back
        if (value) {
            const selDate = parseDateString(value);
            const diffWeeks = Math.ceil((currentMonday - selDate) / (7 * 24 * 60 * 60 * 1000));
            if (diffWeeks > weeksBack) {
                weeksBack = Math.min(diffWeeks + 4, 52);
            }
        }

        const pages = [];
        for (let i = -weeksBack; i <= 0; i++) {
            const monday = new Date(currentMonday);
            monday.setDate(currentMonday.getDate() + i * 7);
            pages.push(getWeekDaysForMonday(monday));
        }
        return pages;
    }, [value]);

    // Active month label based on currently viewed week
    const [activeMonthLabel, setActiveMonthLabel] = useState(() => {
        const d = value ? parseDateString(value) : new Date();
        return d.toLocaleDateString(lang, { month: "long", year: "numeric" });
    });

    // Formatted display of selected date on the far left (dd/mm/yyyy)
    const selectedDateDisplay = useMemo(() => {
        const target = value || todayStr;
        if (!target) return "";
        try {
            const [y, m, d] = target.split("-");
            if (y && m && d) {
                return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
            }
            return target;
        } catch {
            return target;
        }
    }, [value, todayStr]);

    // Scroll to the active week containing value or today
    const scrollToWeekOfDate = (targetDateStr, smooth = false) => {
        const el = weekScrollRef.current;
        if (!el || el.clientWidth === 0) return;

        const targetIndex = weekPages.findIndex((week) =>
            week.some((d) => formatDateString(d) === targetDateStr)
        );
        const pageIdx = targetIndex !== -1 ? targetIndex : weekPages.length - 1;

        isProgrammaticScroll.current = true;
        if (smooth) {
            el.scrollTo({ left: el.clientWidth * pageIdx, behavior: "smooth" });
        } else {
            el.scrollLeft = el.clientWidth * pageIdx;
        }

        const midDay = weekPages[pageIdx]?.[3] || new Date();
        setActiveMonthLabel(midDay.toLocaleDateString(lang, { month: "long", year: "numeric" }));

        setTimeout(() => {
            isProgrammaticScroll.current = false;
        }, 250);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            scrollToWeekOfDate(value || todayStr, false);
        }, 30);

        const handleResize = () => {
            scrollToWeekOfDate(value || todayStr, false);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekPages]);

    const handleScroll = () => {
        if (isProgrammaticScroll.current) return;
        const el = weekScrollRef.current;
        if (!el || el.clientWidth === 0) return;

        const pageIdx = Math.round(el.scrollLeft / el.clientWidth);
        const activeWeek = weekPages[pageIdx];
        if (activeWeek && activeWeek[3]) {
            const midDay = activeWeek[3];
            setActiveMonthLabel(midDay.toLocaleDateString(lang, { month: "long", year: "numeric" }));
        }
    };

    const handleWeekNav = (dir) => {
        const el = weekScrollRef.current;
        if (!el || el.clientWidth === 0) return;
        el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
    };

    return (
        <div className={`w-full min-w-0 max-w-full overflow-hidden ${className}`}>
            {label && (
                <span className="block text-xs sm:text-sm font-normal mb-1.5 text-foreground-light dark:text-foreground-dark">
                    {label}
                </span>
            )}

            <div className="w-full min-w-0">
                {/* Header: Selected date on left (dd/mm/yyyy), Month & Buttons on right */}
                <div className="flex items-center justify-between mb-2">
                    {/* Far left: formatted selected date (dd/mm/yyyy, not green) */}
                    <span className="text-xs sm:text-sm font-medium text-foreground-light dark:text-foreground-dark shrink-0">
                        {selectedDateDisplay}
                    </span>

                    {/* Right: Month, then Prev and Next buttons */}
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        <span className="text-xs sm:text-sm font-medium capitalize text-subtle-light dark:text-subtle-dark mr-1">
                            {activeMonthLabel}
                        </span>

                        {/* Prev Week */}
                        <button
                            type="button"
                            onClick={() => handleWeekNav(-1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-subtle-light dark:text-subtle-dark hover:text-foreground-light dark:hover:text-foreground-dark hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                            aria-label="Previous week"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* Next Week */}
                        <button
                            type="button"
                            onClick={() => handleWeekNav(1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-subtle-light dark:text-subtle-dark hover:text-foreground-light dark:hover:text-foreground-dark hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                            aria-label="Next week"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Weekday Strip Carousel */}
                <div className="w-full min-w-0 overflow-hidden">
                    <div
                        ref={weekScrollRef}
                        onScroll={handleScroll}
                        className="w-full flex overflow-x-auto snap-x snap-mandatory touch-pan-x overscroll-x-contain [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: "none" }}
                    >
                        {weekPages.map((week, pageIdx) => (
                            <div
                                key={pageIdx}
                                className="w-full min-w-full shrink-0 snap-center snap-always grid grid-cols-7 gap-1 text-center justify-items-center"
                            >
                                {week.map((day) => {
                                    const dateStr = formatDateString(day);
                                    const effectiveDate = value || todayStr;
                                    const isSelected = dateStr === effectiveDate;
                                    const isToday = dateStr === todayStr;
                                    const isFuture = maxDate && dateStr > maxDate;
                                    const dayNameShort = day.toLocaleDateString(lang, { weekday: "short" });
                                    const dayNum = day.getDate();

                                    return (
                                        <button
                                            key={dateStr}
                                            type="button"
                                            disabled={isFuture}
                                            onClick={() => onChange?.(dateStr)}
                                            className="flex flex-col items-center justify-center rounded py-1 transition-all w-full outline-none select-none cursor-pointer"
                                        >
                                            <span className="text-xs font-medium uppercase tracking-wide text-subtle-light dark:text-subtle-dark mb-1">
                                                {dayNameShort.slice(0, 3)}
                                            </span>
                                            <div
                                                className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-all ${
                                                    isSelected
                                                        ? "bg-primary text-white shadow-sm"
                                                        : isToday
                                                        ? "text-primary border border-primary/40 font-medium"
                                                        : isFuture
                                                        ? "opacity-20 cursor-not-allowed text-subtle-light dark:text-subtle-dark"
                                                        : "text-foreground-light dark:text-foreground-dark hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                                }`}
                                            >
                                                {dayNum}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
